type AssetManifest = {
    files?: Record<string, string>;
};

type VersionFetch = (
    input: string,
    init?: RequestInit
) => Promise<{
    ok: boolean;
    status: number;
    json: () => Promise<AssetManifest>;
}>;

export interface AppVersionMonitorOptions {
    appName: string;
    checkIntervalMs?: number;
    fetcher?: VersionFetch;
    manifestPath?: string;
    now?: () => number;
    reload?: (url: string) => void;
}

const DEFAULT_CHECK_INTERVAL_MS = 5 * 60 * 1000;

const getPublicUrl = () => (process.env.PUBLIC_URL || '').replace(/\/$/, '');

const normalizePath = (value: string) => {
    try {
        return new URL(value, window.location.origin).pathname;
    } catch {
        return value;
    }
};

export const getCurrentMainBundlePath = () => {
    const scripts = Array.from(document.scripts)
        .map((script) => script.getAttribute('src') || script.src)
        .filter(Boolean);

    const mainScript = scripts.find((src) =>
        /\/static\/js\/main\.[^/]+\.js$/.test(normalizePath(src))
    );

    return mainScript ? normalizePath(mainScript) : undefined;
};

const getManifestUrl = (manifestPath: string | undefined, now: () => number) => {
    const path = manifestPath || `${getPublicUrl()}/asset-manifest.json`;
    const url = new URL(path, window.location.origin);
    url.searchParams.set('_', String(now()));
    return url.toString();
};

const getLatestMainBundlePath = async (
    fetcher: VersionFetch,
    manifestPath: string | undefined,
    now: () => number
) => {
    const response = await fetcher(getManifestUrl(manifestPath, now), {
        cache: 'no-store',
        credentials: 'same-origin',
        headers: {
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache',
        },
    });

    if (!response.ok) {
        throw new Error(`Unable to load asset manifest (${response.status}).`);
    }

    const manifest = await response.json();
    const latestMainBundle = manifest.files?.['main.js'];

    if (!latestMainBundle) {
        throw new Error('Asset manifest did not contain a main.js entry.');
    }

    return normalizePath(latestMainBundle);
};

const getReloadStorageKey = (appName: string) => `coalesce:${appName}:reload-for-main-bundle`;

const getCacheBustedUrl = (now: () => number) => {
    const url = new URL(window.location.href);
    url.searchParams.set('appVersion', String(now()));
    return url.toString();
};

export const checkForAppUpdate = async ({
    appName,
    fetcher = fetch as VersionFetch,
    manifestPath,
    now = Date.now,
    reload = (url) => window.location.replace(url),
}: AppVersionMonitorOptions) => {
    try {
        const currentMainBundle = getCurrentMainBundlePath();

        if (!currentMainBundle) {
            return;
        }

        const latestMainBundle = await getLatestMainBundlePath(fetcher, manifestPath, now);
        const reloadStorageKey = getReloadStorageKey(appName);

        if (currentMainBundle === latestMainBundle) {
            window.sessionStorage.removeItem(reloadStorageKey);
            return;
        }

        if (window.sessionStorage.getItem(reloadStorageKey) === latestMainBundle) {
            console.warn('[AppVersion] Latest UI version still not loaded after reload attempt', {
                appName,
                currentMainBundle,
                latestMainBundle,
            });
            return;
        }

        window.sessionStorage.setItem(reloadStorageKey, latestMainBundle);
        const nextUrl = getCacheBustedUrl(now);

        console.info('[AppVersion] New UI version detected; reloading', {
            appName,
            currentMainBundle,
            latestMainBundle,
            nextUrl,
        });

        reload(nextUrl);
    } catch (error) {
        console.warn('[AppVersion] Could not check for latest UI version', {
            appName,
            message: error instanceof Error ? error.message : 'Unknown version check error.',
            error,
        });
    }
};

export const startAppVersionMonitor = (options: AppVersionMonitorOptions) => {
    // if (process.env.NODE_ENV !== 'production') {
    //     return () => {};
    // }

    const check = () => {
        void checkForAppUpdate(options);
    };

    const checkWhenVisible = () => {
        if (document.visibilityState === 'visible') {
            check();
        }
    };

    const intervalId = window.setInterval(
        check,
        options.checkIntervalMs ?? DEFAULT_CHECK_INTERVAL_MS
    );

    window.addEventListener('focus', check);
    document.addEventListener('visibilitychange', checkWhenVisible);
    check();

    return () => {
        window.clearInterval(intervalId);
        window.removeEventListener('focus', check);
        document.removeEventListener('visibilitychange', checkWhenVisible);
    };
};
